import { createRouter, createWebHistory } from 'vue-router'
import NewsListPage from '../ui/pages/main/NewsListPage.vue'
import ArticlePage from '../ui/pages/article/article-page.vue'
import RedirectPlaceholder from '../ui/pages/_redirect/RedirectPlaceholder.vue'
import { getCurrentUserStore } from '../store/services'
import { TranslationMode } from '@lingvo-news/contracts'
import { parseCommonRouteParamsFromParams } from '../composables/useCommonRouteParams';
import { getRouteParamsForStoredPreferences, readStoredPreferences, type StoredPreferences } from '../composables/usePreferencesStorage';
import { captureInvitedByUserId } from '../referrals/invite-links';

const routes = [
	{ path: '/login', name: 'login', component: () => import('../ui/pages/auth/LoginPage.vue'), meta: { requiresAuth: false } },
	{ path: '/auth/login', redirect: '/login' },
	{ path: '/auth/register', name: 'register', component: () => import('../ui/pages/auth/RegisterPage.vue'), meta: { requiresAuth: false } },
	{ path: '/auth/forgot-password', name: 'forgot-password', component: () => import('../ui/pages/auth/ForgotPasswordPage.vue'), meta: { requiresAuth: false } },
	{ path: '/auth/reset-password', name: 'reset-password', component: () => import('../ui/pages/auth/ResetPasswordPage.vue'), meta: { requiresAuth: false } },
	{ path: '/auth/verify-email', name: 'verify-email', component: () => import('../ui/pages/auth/VerifyEmailPage.vue'), meta: { requiresAuth: false } },
	{ path: '/auth/verify-email-result', name: 'verify-email-result', component: () => import('../ui/pages/auth/VerifyEmailResultPage.vue'), meta: { requiresAuth: false } },
	{ path: '/auth/verify-email-sent', name: 'verify-email-sent', component: () => import('../ui/pages/auth/VerifyEmailSentPage.vue'), meta: { requiresAuth: false } },
	{ path: '/telegram-email-confirmation', name: 'telegram-email-confirmation', component: () => import('../ui/pages/telegram-email-connection/TelegramEmailConnectionConfirmationPage.vue'), meta: { requiresAuth: false } },
	{ path: '/privacy', name: 'privacy', component: () => import('../ui/pages/legal/PrivacyPolicyPage.vue'), meta: { requiresAuth: false } },
	{ path: '/terms', name: 'terms', component: () => import('../ui/pages/legal/TermsOfServicePage.vue'), meta: { requiresAuth: false } },
	{ path: '/contacts', name: 'contacts', component: () => import('../ui/pages/legal/ContactsPage.vue'), meta: { requiresAuth: false } },
	{ path: '/feedback', name: 'feedback', component: () => import('../ui/pages/feedback/FeedbackPage.vue'), meta: { requiresAuth: true } },
	{
		path: '/quiz',
		name: 'quiz-root',
		component: RedirectPlaceholder,
		meta: { requiresAuth: true, requiresLanguages: true, redirectTo: 'quiz' },
	},
	{ path: '/quiz/:nativeLanguage/:learnedLanguage', name: 'quiz', component: () => import('../ui/quizz/QuizPopupExample.vue'), meta: { requiresAuth: true, requiresLanguages: true } },
	{
		path: '/my-words',
		name: 'my-words-root',
		component: RedirectPlaceholder,
		meta: { requiresAuth: true, requiresLanguages: true, requiresMode: true, redirectTo: 'word-collection' },
	},
	{
		path: '/my-words/:nativeLanguage/:learnedLanguage/:mode',
		name: 'word-collection',
		component: () => import('../ui/pages/word-collection/WordCollectionPage.vue'),
		meta: { requiresAuth: true, requiresLanguages: true, requiresMode: true }
	},
	{
		path: '/repeat',
		name: 'repeat-root',
		component: RedirectPlaceholder,
		meta: { requiresAuth: true, requiresLanguages: true, requiresMode: true, redirectTo: 'word-repeat' },
	},
	{
		path: '/repeat/:nativeLanguage/:learnedLanguage/:mode',
		name: 'word-repeat',
		component: () => import('../ui/pages/repeat/repeat-words-page.vue'),
		meta: { requiresAuth: true, requiresLanguages: true, requiresMode: true }
	},
	{
		path: '/daily-words',
		name: 'daily-words-root',
		component: RedirectPlaceholder,
		meta: { requiresAuth: true, requiresLanguages: true, requiresMode: true, redirectTo: 'daily-words' },
	},
	{
		path: '/daily-words/:nativeLanguage/:learnedLanguage/:mode',
		name: 'daily-words',
		component: () => import('../ui/pages/daily-words/DailyWordsPage.vue'),
		meta: { requiresAuth: true, requiresLanguages: true, requiresMode: true }
	},
	{
		path: '/',
		name: 'root',
		component: RedirectPlaceholder,
		meta: { requiresAuth: false },
	},
	{
		path: '/home/:nativeLanguage/:learnedLanguage/:mode/:categoryId?',
		name: 'home',
		component: NewsListPage,
		meta: { requiresAuth: false, requiresLanguages: true, requiresMode: true }
	},
	{
		path: '/article/:nativeLanguage/:learnedLanguage/:mode/:categoryId/:id',
		name: 'article',
		component: ArticlePage,
		meta: { requiresAuth: false, requiresLanguages: true, requiresMode: true }
	},
	{
		path: '/user-categories',
		name: 'user-categories',
		component: () => import('../ui/pages/user-categories/UserCategoriesPage.vue'),
		meta: { requiresAuth: true }
	},
	{
		path: '/my-settings',
		name: 'my-settings',
		component: () => import('../ui/pages/user-categories/MySettingsPage.vue'),
		meta: { requiresAuth: true }
	},
	{ path: '/settings', redirect: '/my-settings' },
	{
		path: '/email-settings',
		name: 'email-settings',
		component: () => import('../ui/pages/telegram-email-connection/EmailSettingsPage.vue'),
		meta: { requiresAuth: true }
	},
	{
		path: '/my-subscription',
		name: 'my-subscription',
		component: () => import('../ui/pages/telegram-email-connection/TelegramEmailProfilePage.vue'),
		meta: { requiresAuth: true }
	},
	{ path: '/profile', redirect: '/my-subscription' },
	{ path: '/telegram-profile', redirect: '/my-subscription' },
	{
		path: '/list/:nativeLanguage/:learnedLanguage/:mode/:categoryId?',
		name: 'news-list',
		component: NewsListPage,
		meta: { requiresAuth: false, requiresLanguages: true, requiresMode: true }
	},
	{
		path: '/intro',
		name: 'intro',
		component: () => import('../ui/pages/intro/IntroScreen.vue'),
		meta: { requiresAuth: false }
	},
	{
		path: '/admin/news-mnemonics',
		name: 'admin-news-mnemonics',
		component: () => import('../ui/pages/admin/NewsMnemonicsAdminPage.vue'),
		meta: { requiresAuth: true }
	},
	{
		path: '/admin',
		name: 'admin',
		component: () => import('../ui/pages/admin/admin-page.vue'),
		meta: { requiresAuth: true, requiresAdmin: true }
	},
]

export const router = createRouter({
	history: createWebHistory(),
	routes,
})

// Check if user has completed intro preferences
const hasCompletedIntro = (): boolean => {
	return readStoredPreferences() !== null;
};

// Get the appropriate route based on user preferences and mode
const getNewsRouteForUser = () => {
	const preferences = readStoredPreferences();
	if (!preferences) {
		return { name: 'intro' };
	}

	const { nativeLanguage, learnedLanguage, mode } = getRouteParamsForStoredPreferences(preferences);

	return {
		name: 'news-list',
		params: {
			nativeLanguage,
			learnedLanguage,
			mode
		}
	};
};

// Navigation guard to check authentication and admin access
// @ts-ignore - Vue Router types have compatibility issues with this TypeScript version
router.beforeEach(async (to, from, next) => {
	const userStore = getCurrentUserStore();
	captureInvitedByUserId(typeof to.query.invitedByUserId === 'string' ? to.query.invitedByUserId : undefined);

	const needsAuthForRouting =
		to.meta.requiresAuth !== false ||
		to.name === 'root' ||
		to.name === 'home' ||
		to.name === 'login' ||
		to.name === 'register';

	if (needsAuthForRouting) {
		await userStore.initialize();
	} else {
		userStore.initialize();
	}

	const requiresAuth = to.meta.requiresAuth!==false; // Default to true if not specified
	const requiresAdmin = to.meta.requiresAdmin===true;
	const isAuthenticated = userStore.getCurrentUserId()!=null;
	const currentUser = userStore.getCurrentUser();
	const isAdmin = currentUser?.isAdmin===true;

	// Root + language redirects should not rely on defaults; if missing, send user to settings/intro.
	const redirectWhenMissingLanguages = () => {
		next(isAuthenticated ? { name: 'my-settings' } : { name: 'intro' });
	};

	const redirectToPreferenceRoute = (routeName: string, includeMode: boolean) => {
		const preferences = readStoredPreferences();
		if (!preferences) {
			redirectWhenMissingLanguages();
			return;
		}
		const params = getRouteParamsForStoredPreferences(preferences);
		next({
			name: routeName,
			params: includeMode
				? { nativeLanguage: params.nativeLanguage, learnedLanguage: params.learnedLanguage, mode: params.mode }
				: { nativeLanguage: params.nativeLanguage, learnedLanguage: params.learnedLanguage },
		});
	};

	if (to.name === 'root') {
		if (hasCompletedIntro()) {
			next(getNewsRouteForUser());
			return;
		}
		redirectWhenMissingLanguages();
		return;
	}

	if (to.meta.redirectTo && typeof to.meta.redirectTo === 'string') {
		const redirectName = to.meta.redirectTo;
		const includeMode = to.meta.requiresMode === true;
		redirectToPreferenceRoute(redirectName, includeMode);
		return;
	}

	if (to.meta.requiresLanguages === true) {
		const parsed = parseCommonRouteParamsFromParams(to.params);
		const nativeLanguage = parsed.nativeLanguage;
		const learnedLanguage = parsed.learnedLanguage;
		const mode = to.meta.requiresMode === true ? parsed.mode : TranslationMode.Partial;

		const hasValidLanguages = !!nativeLanguage && !!learnedLanguage;
		const hasValidMode = to.meta.requiresMode === true ? mode !== null : true;

		if (!hasValidLanguages || !hasValidMode) {
			const preferences = readStoredPreferences();
			if (preferences) {
				const params = getRouteParamsForStoredPreferences(preferences);
				const nextParams: Record<string, string> = {};

				// Preserve existing params where possible
				for (const [k, v] of Object.entries(to.params)) {
					if (typeof v === 'string') nextParams[k] = v;
				}

				nextParams.nativeLanguage = params.nativeLanguage;
				nextParams.learnedLanguage = params.learnedLanguage;
				if (to.meta.requiresMode === true) {
					nextParams.mode = params.mode;
				}

				next({ name: to.name as string, params: nextParams });
				return;
			}

			redirectWhenMissingLanguages();
			return;
		}

		// Validate the language pair against stored preferences.
		// If the pair in the URL doesn't match what the user configured (i.e. it's unsupported),
		// redirect to intro (unauthenticated) or settings (authenticated).
		const preferences = readStoredPreferences();
		if (preferences) {
			const pairMismatch =
				nativeLanguage !== preferences.nativeLanguage ||
				learnedLanguage !== preferences.learnedLanguage;
			if (pairMismatch) {
				next(isAuthenticated ? { name: 'my-settings' } : { name: 'intro' });
				return;
			}
		} else {
			redirectWhenMissingLanguages();
			return;
		}
	}

	if (requiresAuth && !isAuthenticated) {
		next({ name: 'login' });
	} else if ((to.name==='login' || to.name==='register') && isAuthenticated) {
		// Redirect to appropriate page based on intro completion
		next(hasCompletedIntro() ? getNewsRouteForUser() : { name: 'my-settings' });
	} else if (requiresAdmin && !isAdmin) {
		// Redirect to appropriate page based on intro completion
		next(hasCompletedIntro() ? getNewsRouteForUser() : { name: 'my-settings' });
	} else if (to.name === 'home' && !hasCompletedIntro()) {
		// If trying to access home but hasn't completed intro, redirect to intro
		next(isAuthenticated ? { name: 'my-settings' } : { name: 'intro' });
	} else {
		next();
	}
});

export default router
